import i18n from '../../i18n/i18n';
/**
 * تحويل الأرقام العربية إلى إنجليزية داخل النص
 * @param {string} str
 * @returns {string}
 */
export function convertArabicNumbersToEnglish(str) {
  if (typeof str !== 'string') return str;
  return str.replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));
}

export const BASE_URL = process.env.REACT_APP_BASE_URL;

export const getToken = () => {
  const storedUser = localStorage.getItem('useralbaraqawy');
  if (!storedUser) return null;

  try {
    const parsed = JSON.parse(storedUser);
    if (parsed.token) return parsed.token;
    if (parsed.data?.token) return parsed.data.token;
    if (parsed.result?.token) return parsed.result.token;
    if (parsed.user?.token) return parsed.user.token;
    return null;
  } catch {
    return null;
  }
};

export const getCurrentLanguage = () => i18n.language || 'ar';

/**
 * Get text direction styles based on current language
 * Returns an object with direction, textAlign, and dir properties
 * @returns {Object} { direction: 'rtl'|'ltr', textAlign: 'right'|'left', dir: 'rtl'|'ltr' }
 */
export const getTextDirection = () => {
  const isRTL = i18n.language === 'ar';
  return {
    direction: isRTL ? 'rtl' : 'ltr',
    textAlign: isRTL ? 'right' : 'left',
    dir: isRTL ? 'rtl' : 'ltr'
  };
};

export const toPositiveInteger = (value, fallback = 1) => {
  const normalizedValue = convertArabicNumbersToEnglish(String(value ?? '')).trim();
  const parsedValue = Number.parseInt(normalizedValue, 10);
  const fallbackValue = Number.parseInt(fallback, 10);
  return Number.isFinite(parsedValue) && parsedValue > 0
    ? parsedValue
    : (Number.isFinite(fallbackValue) && fallbackValue > 0 ? fallbackValue : 1);
};

export const normalizeSortOrder = (sortOrder) => (sortOrder === 'desc' ? 'desc' : 'asc');

/**
 * Shared active/inactive filter matcher.
 * Backends return is_active as true/false, 1/0 or "1"/"0" — filter dropdowns
 * use "1"/"0". Comparing String(value) to the filter breaks for booleans,
 * so normalize both sides to a boolean first.
 * @param {*} value        record's is_active (bool | number | string)
 * @param {string} filter  '' (all) | '1' (active) | '0' (inactive)
 * @returns {boolean} whether the record passes the filter
 */
export const matchesActiveFilter = (value, filter) => {
  if (filter === '' || filter === null || filter === undefined) return true;
  const isActive = value === true || value === 1 || value === '1';
  return isActive === (String(filter) === '1');
};

/** Normalize API payloads to a plain array (prevents items.map crashes). */
export const extractArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return [];

  const arrayKeys = ['data', 'items', 'results', 'records', 'accounts', 'transactions', 'entries', 'movements'];
  for (const key of arrayKeys) {
    if (Array.isArray(value[key])) return value[key];
  }

  for (const key of arrayKeys) {
    const nested = extractArray(value[key]);
    if (nested.length) return nested;
  }

  return [];
};

/** Resolve current-period execution quantity from varying API field names. */
export const getCurrentExecutionQty = (item = {}) => {
  const contractItem = item.contract_item || {};
  return Number(
    item.current_qty ??
    item.current_quantity ??
    item.qty ??
    item.quantity ??
    item.executed_qty ??
    item.execution_qty ??
    item.this_period_qty ??
    item.to_be_executed_qty ??
    contractItem.current_qty ??
    contractItem.current_quantity ??
    0
  ) || 0;
};

/** Resolve previously executed quantity from varying API field names. */
export const getPreviouslyExecutedQty = (item = {}) => {
  const contractItem = item.contract_item || {};
  return Number(
    item.previous_qty ??
    item.previously_executed_qty ??
    item.previous_quantity ??
    item.executed_before_qty ??
    item.prior_qty ??
    item.prev_qty ??
    contractItem.previous_qty ??
    contractItem.previously_executed_qty ??
    0
  ) || 0;
};

export const sortListById = (items, sortOrder = 'asc') => {
  if (!Array.isArray(items)) return [];
  const direction = normalizeSortOrder(sortOrder) === 'desc' ? -1 : 1;
  return [...items].sort((a, b) => {
    const left = Number(a?.id ?? 0);
    const right = Number(b?.id ?? 0);
    return (left - right) * direction;
  });
};

/**
 * Get PDF template styles for label-value rows
 * In LTR (English): Labels on left, Values on right
 * In RTL (Arabic): Labels on right, Values on left
 * @param {boolean} isRTL - Whether the language is RTL
 * @returns {Object} Styles object for PDF template rows
 */
export const getPdfRowStyles = isRTL => {
  return {
    row: {
      display: "flex",
      borderBottom: "1px solid #000",
      minHeight: "38px",
      flexDirection: "row",
      direction: "ltr", // Force LTR on flex container to prevent RTL direction from affecting layout
    },
    label: {
      width: "30%",
      borderLeft: isRTL ? "none" : "1px solid #000",
      borderRight: isRTL ? "1px solid #000" : "none",
      backgroundColor: "rgba(227, 242, 253, 0.4)",
      display: "flex",
      alignItems: "center",
      justifyContent: isRTL ? "flex-end" : "flex-start", // LTR: left align, RTL: right align
      textAlign: isRTL ? "right" : "left", // Arabic: right, English: left
      fontWeight: "700",
      fontSize: "14px",
      direction: isRTL ? "rtl" : "ltr",
      color: "#1976d2",
      fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
      padding: "0 10px",
    },
    value: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: isRTL ? "flex-end" : "flex-start", // LTR: left align, RTL: right align
      textAlign: isRTL ? "right" : "left", // LTR: left, RTL: right
      fontSize: "15px",
      direction: isRTL ? "rtl" : "ltr",
      padding: "0 10px",
      backgroundColor: "rgba(255, 255, 255, 0.3)",
      fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
      fontWeight: "bold",
    },
  };
};

/**
 * Split array into chunks for PDF pagination
 * @param {Array} array - Array to split
 * @param {number} chunkSize - Size of each chunk (default: 20)
 * @returns {Array} Array of chunks
 */
export const chunkArray = (array, chunkSize = 20) => {
  if (!Array.isArray(array)) return [];
  if (array.length === 0) return [];
  const chunks = [];
  const safeChunkSize = toPositiveInteger(chunkSize, 20);
  for (let i = 0; i < array.length; i += safeChunkSize) {
    chunks.push(array.slice(i, i + safeChunkSize));
  }
  return chunks;
};

/**
 * Calculate dynamic PDF page chunks based on offsetHeight measurements of DOM elements.
 *
 * @param {Array} items - The list of items to chunk.
 * @param {Object} elements - The DOM elements to measure height from.
 * @param {HTMLElement} elements.topHeader - The top header DOM element.
 * @param {HTMLElement} elements.thead - The table header DOM element.
 * @param {HTMLElement} elements.footer - The footer DOM element.
 * @param {HTMLElement} elements.tbody - The table body DOM element.
 * @param {Object} options - Configuration options for height calculation.
 * @param {number} [options.pageHeight=1033] - Total inner height of the PDF container.
 * @param {number} [options.containerTopPadding=18] - Top padding of the PDF page container.
 * @param {number} [options.containerBottomPadding=10] - Bottom padding of the PDF page container.
 * @param {number} [options.safeMargin=15] - Safety margin before the footer.
 * @param {number} [options.fallbackSize=25] - Fallback chunk size if DOM elements cannot be measured.
 * @param {number} [options.defaultHeaderHeight=180] - Default header height if measured as 0.
 * @param {number} [options.defaultTableHeaderHeight=32] - Default table header height if measured as 0.
 * @param {number} [options.defaultFooterHeight=80] - Default footer height if measured as 0.
 * @param {number} [options.defaultRowHeight=28] - Default row height if measured as 0.
 * @returns {Array<{items: Array, startIndex: number}>} Array of chunks with start indices.
 */
export const calculatePdfPagination = (items, elements, options = {}) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const {
    pageHeight = 1033,
    containerTopPadding = 18,
    containerBottomPadding = 10,
    safeMargin = 15,
    fallbackSize = 25,
    defaultHeaderHeight = 180,
    defaultTableHeaderHeight = 32,
    defaultFooterHeight = 80,
    defaultRowHeight = 28,
  } = options;

  const { topHeader, thead, footer, tbody } = elements || {};

  if (!topHeader || !thead || !footer || !tbody) {
    return chunkArray(items, fallbackSize).map((chunkItems, pageIndex) => ({
      items: chunkItems,
      startIndex: pageIndex * fallbackSize,
    }));
  }

  // Structural sections (banner header, table header row, footer reserve) have
  // fixed, content-independent markup, so their real height should always sit
  // close to the supplied default. If the browser reports something far larger
  // than that (e.g. a header/footer <img> that hadn't finished decoding yet
  // when offsetHeight was read, momentarily collapsing/expanding its layout
  // box), trust the default instead of the bad reading. Without this guard a
  // single inflated measurement silently shrinks the usable row area for every
  // page and the table stops filling the page long before it reaches the
  // footer, leaving a large blank gap.
  const clampStructuralHeight = (measured, fallback) => {
    if (!measured || measured <= 0) return fallback;
    const maxAllowed = fallback * 1.6;
    return measured > maxAllowed ? fallback : measured;
  };

  const headerHeight = clampStructuralHeight(topHeader.offsetHeight, defaultHeaderHeight);
  const tableHeaderHeight = clampStructuralHeight(thead.offsetHeight, defaultTableHeaderHeight);
  const footerHeight = clampStructuralHeight(footer.offsetHeight, defaultFooterHeight);

  const footerStartY =
    pageHeight - containerBottomPadding - footerHeight - safeMargin;
  const rowElements = Array.from(tbody.querySelectorAll("tr"));

  const chunks = [];
  let currentItems = [];
  let currentStartIndex = 0;
  let currentY = containerTopPadding + headerHeight + tableHeaderHeight;

  items.forEach((item, index) => {
    const rowEl = rowElements[index] || rowElements[rowElements.length - 1];
    const rowHeight = rowEl.offsetHeight || defaultRowHeight;

    const remainingSpace = footerStartY - currentY;

    if (remainingSpace >= rowHeight) {
      currentItems.push(item);
      currentY += rowHeight;
    } else {
      if (currentItems.length > 0) {
        chunks.push({ items: currentItems, startIndex: currentStartIndex });
        currentItems = [item];
        currentStartIndex = index;
        currentY =
          containerTopPadding + headerHeight + tableHeaderHeight + rowHeight;
      } else {
        // Force add to avoid infinite loop for exceptionally tall rows
        currentItems.push(item);
        currentY += rowHeight;
      }
    }
  });

  if (currentItems.length > 0) {
    chunks.push({ items: currentItems, startIndex: currentStartIndex });
  }

  return chunks;
};
